import { DateOnly } from '@if/core/date'
import { EPaymentFrequency, EPaymentPlan } from '@if/core/types'
import * as yup from '@if/core/yup'
import { BaseWorksheetDto } from '@src/data/types/BaseWorksheetSchema'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseWorksheetFormConfig, findMissingFinancingConfigKeys } from './BaseWorksheetForm.config'

export interface BaseWorksheetFormValues {
  amountRequested: number
  paymentPlanId: string
  term: number | null
  paymentFrequency: EPaymentFrequency
  activationDate: DateOnly
  firstPaymentDate: DateOnly | null
  includeProtection: boolean
  isHighEstimate: boolean
}

export interface TermValidationContext {
  validTerms: Set<number>
  maxPmtAmount: number | undefined
}

export const buildBaseWorksheetFormInitialValues = <T extends BaseWorksheetFormValues>(
  defaultValues: T,
  worksheet: BaseWorksheetDto | null,
  requestedLoanAmount: number | null,
  defaultPaymentPlanId?: EPaymentPlan,
): T => {
  return {
    ...defaultValues,
    amountRequested: worksheet?.amountRequested ?? requestedLoanAmount ?? defaultValues.amountRequested,
    paymentPlanId:
      (worksheet?.paymentPlanId as EPaymentPlan | undefined) ??
      defaultPaymentPlanId ??
      EPaymentPlan.RegularDailyInterests,
    term: worksheet?.term ?? defaultValues.term,
    paymentFrequency: worksheet?.paymentFrequency ?? defaultValues.paymentFrequency,
    activationDate: worksheet?.deliveryOn ? new DateOnly(worksheet.deliveryOn) : defaultValues.activationDate,
    firstPaymentDate: worksheet?.firstPaymentOn
      ? new DateOnly(worksheet.firstPaymentOn)
      : defaultValues.firstPaymentDate,
    includeProtection: worksheet?.includeInsurance || worksheet?.includeProtection || defaultValues.includeProtection,
    isHighEstimate: worksheet?.isHighEstimate ?? defaultValues.isHighEstimate,
  }
}

export const useBaseWorksheetFormInitialValues = <T extends BaseWorksheetFormValues>(
  defaultValues: T | null,
  worksheet: BaseWorksheetDto | null,
  requestedLoanAmount: number | null,
  defaultPaymentPlanId?: EPaymentPlan,
): T | undefined =>
  useMemo(() => {
    if (!defaultValues) return undefined
    return buildBaseWorksheetFormInitialValues(defaultValues, worksheet, requestedLoanAmount, defaultPaymentPlanId)
  }, [defaultPaymentPlanId, defaultValues, requestedLoanAmount, worksheet])

export const useBaseWorksheetFormFields = (config: BaseWorksheetFormConfig) => {
  const { t } = useTranslation()
  const {
    minLoanAmount: configMinLoanAmount,
    maxLoanAmount: configMaxLoanAmount,
    supportedPaymentFrequencies,
    expiresOn: expiresOnRaw,
    maxAmountFinanced,
    isSinglePayment,
    availablePaymentPlans,
    isCustomPromoPlanMerchant,
  } = config

  return useMemo(() => {
    const supportedFrequencies = isSinglePayment ? [EPaymentFrequency.Single] : supportedPaymentFrequencies

    const minLoanAmount = configMinLoanAmount > 0 ? configMinLoanAmount : 1
    const loanAmountCaps: number[] = []
    if (configMaxLoanAmount > 0) loanAmountCaps.push(configMaxLoanAmount)
    if (maxAmountFinanced && maxAmountFinanced > 0) loanAmountCaps.push(maxAmountFinanced)
    const maxLoanAmount = loanAmountCaps.length > 0 ? Math.min(...loanAmountCaps) : null

    const today = DateOnly.today()
    const expiresOn = new DateOnly(expiresOnRaw)

    const defaultPaymentFrequency = isSinglePayment
      ? EPaymentFrequency.Single
      : supportedFrequencies.includes(EPaymentFrequency.Monthly)
        ? EPaymentFrequency.Monthly
        : supportedFrequencies[0]

    const amountRequestedBase = yup
      .number()
      .required()
      .min(minLoanAmount, t('worksheetCommon.errors.amountTooLow', { min: minLoanAmount }))
    const amountRequested =
      maxLoanAmount !== null
        ? amountRequestedBase
            .max(maxLoanAmount, t('worksheetCommon.errors.amountTooHigh', { max: maxLoanAmount }))
            .default(minLoanAmount)
        : amountRequestedBase.default(minLoanAmount)

    return {
      amountRequested,
      paymentPlanId: yup
        .string()
        .required()
        .test('plan-available', t('worksheetCommon.errors.paymentPlanNotAvailable'), (value) => {
          if (!value || value === (EPaymentPlan.RegularDailyInterests as string)) return true
          // On a promoPlan merchant the legacy select is hidden, so a non-Regular value is a promoPlan GUID
          // (current or superseded) that the backend resolves via GetResolvedPlan.
          if (isCustomPromoPlanMerchant) return true
          return availablePaymentPlans.some((p) => (p.id as string) === value)
        })
        .default(''),
      term: yup
        .number()
        .integer()
        .nullable()
        .required()
        .test('term-available', function (value) {
          const ctx = this.options.context?.term as TermValidationContext | undefined
          if (!ctx || value == null || ctx.validTerms.has(value)) return true
          return this.createError({
            message:
              ctx.validTerms.size === 0
                ? t('worksheetCommon.errors.noValidTerm', { maxPayment: ctx.maxPmtAmount })
                : t('worksheetCommon.errors.pmtTooHigh', { term: value, maxPayment: ctx.maxPmtAmount }),
          })
        })
        .default(() => null),
      paymentFrequency: yup
        .mixed<EPaymentFrequency>()
        .required()
        .oneOf(supportedFrequencies, t('worksheetCommon.errors.paymentFrequencyNotSupported'))
        .default(defaultPaymentFrequency),
      activationDate: yup
        .dateOnly()
        .required()
        .test('not-in-past', t('worksheetCommon.errors.activationInPast'), (value) => {
          if (!value) return true
          return value >= today
        })
        .test('before-expiry', t('worksheetCommon.errors.activationAfterExpiry'), (value) => {
          if (!value) return true
          return value <= expiresOn
        })
        .default(() => DateOnly.today()),
      firstPaymentDate: yup
        .dateOnly()
        .nullable()
        .required()
        .default(() => null),
      includeProtection: yup.boolean().required().default(false),
      isHighEstimate: yup.boolean().required().default(false),
    }
  }, [
    configMinLoanAmount,
    configMaxLoanAmount,
    supportedPaymentFrequencies,
    maxAmountFinanced,
    expiresOnRaw,
    isSinglePayment,
    availablePaymentPlans,
    isCustomPromoPlanMerchant,
    t,
  ])
}

export const useBaseWorksheetFormReady = (config: BaseWorksheetFormConfig): boolean => {
  const { supportedTerms, supportedPaymentFrequencies, isSinglePayment } = config
  return useMemo(() => {
    if (findMissingFinancingConfigKeys(config).length > 0) return false
    if (!supportedTerms?.length) return false
    if (!isSinglePayment && !supportedPaymentFrequencies?.length) return false
    return true
  }, [config, supportedTerms, supportedPaymentFrequencies, isSinglePayment])
}
